import { Client, contractAddress } from "@verified-network/verified-sdk";
import axios from "axios";
import config from "../../services/config/homestead.json";
import {
  biconomyPaymasterEndpoint,
  biconomyWhitelistEndpoint,
  defaultVerifiedUrl,
  emailSenderUrl,
  networkLoanConstant,
  networks,
  smsSenderUrl,
  verifiedGcexUrl,
  verifiedGTNBaseUrl,
} from "../constants";
import { NETWORKS_INFO } from "./networks";
import { ethers } from "ethers";
import ERC20ABI from "../abi/ERC20.json";
import CLIENTABI from "../abi/Client.json";
import { CID } from "multiformats/cid";
import { base58btc } from "multiformats/bases/base58";
import { create } from "multiformats/hashes/digest";
import { keccak256 } from "viem";

export const BUNDLE_ID = "1";

export const timeframeOptions = {
  FOUR_HOURS: "4 hours",
  ONE_DAY: "1 day",
  THERE_DAYS: "3 days",
  WEEK: "1 week",
  MONTH: "1 month",
  // THREE_MONTHS: '3 months',
  // YEAR: '1 year',
  ALL_TIME: "All time",
};

export const Network = {
  MAINNET: 1,
  ROPSTEN: 3,
  RINKEBY: 4,
  GÖRLI: 5,
  KOVAN: 42,
  POLYGON: 137,
  ARBITRUM: 42161,
};

export const getKycStatus = async (address, chainId, web3) => {
  const clientAddress = contractAddress[chainId].Client;
  const clientContract = new web3.eth.Contract(CLIENTABI, clientAddress);
  const kycDataResponse = await clientContract.methods
    .getClientKYC(address)
    .call({ from: address });
  const result = kycDataResponse;
  return Number(result[3]);
};

export const formatNumberWithUnits = (numberToFormat, decimals) => {
  if (isNaN(Number(numberToFormat))) {
    return "0.00";
  }
  const units = ["", "K", "M", "B", "T", "P"];
  let unitIdx = 0;

  for (
    numberToFormat;
    Number(numberToFormat) >= 1000 && unitIdx < units.length - 1;
    unitIdx++
  ) {
    numberToFormat = Number(numberToFormat) / 1000;
  }

  if (decimals === undefined) {
    return Number(numberToFormat).toFixed(2) + units[unitIdx];
  } else if (Number(decimals) === 0) {
    return numberToFormat + units[unitIdx];
  } else if (Number(decimals) > 0) {
    return Number(numberToFormat).toFixed(decimals) + units[unitIdx];
  }
};

export const formatTimestamp = (timestamp, isBlockchainTime) => {
  let totalSeconds = isBlockchainTime
    ? Number(timestamp)
    : Math.floor(Number(timestamp) / 1000);

  if (totalSeconds < 0) {
    return "0 day, 0hour, 0min, 0second";
  }

  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${days} day(s), ${hours}hour(s), ${minutes}min(s), ${seconds}second(s)`;
};

//Todo: add more login to handle token name to accepted token ticker on coinbase
export const getTokenTicker = (tokenName) => {
  if (tokenName.toLowerCase().startsWith("w")) {
    return tokenName.slice(1, tokenName.length);
  } else {
    return tokenName;
  }
};

export const generateSecureCloidHyper = (orderId, isStoploss, isClose) => {
  const standardizedInput = orderId.startsWith("0x") ? orderId : `0x${orderId}`;

  const fullHash = keccak256(
    isStoploss
      ? `${standardizedInput}${ethers.utils.formatBytes32String("STOP")}`
      : isClose
        ? `${standardizedInput}${ethers.utils.formatBytes32String("CLOSE")}`
        : standardizedInput,
  );

  return fullHash.slice(0, 34);
};

const calculateCollectedFee = (size, price, feeRatePercentage) => {
  const notionalFloat = Number(size) * Number(price);
  const notionalSubunits = BigInt(
    ethers.utils.parseUnits(notionalFloat.toFixed(6), "6"),
  );

  const SCALE_FACTOR = 1_000_000n;

  const feeRateInteger = BigInt(
    Math.round(Number(feeRatePercentage) * Number(SCALE_FACTOR)),
  );

  return (notionalSubunits * feeRateInteger) / SCALE_FACTOR;
};

export const getAndCalculateLiveFeesHyper = async (
  chainId,
  tradeSize,
  tradePrice,
  isMarketOrder,
  isStoploss,
) => {
  const isTestnet = Object.keys(networks)?.includes("11155111");
  const url = isTestnet
    ? "https://api.hyperliquid-testnet.xyz/info"
    : "https://api.hyperliquid.xyz/info";

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "userFees",
      user: networkLoanConstant[chainId]?.Brokerages?.HYPERLIQUID,
    }),
  });

  const data = await response?.json();

  const liveTakerRate = data?.feeSchedule.cross;
  const liveMakerRate = data?.feeSchedule.add;

  const activeRateToUse = isMarketOrder ? liveTakerRate : liveMakerRate;

  const dynamicFeeInSubunits = calculateCollectedFee(
    tradeSize,
    tradePrice,
    activeRateToUse,
  );

  return Number(
    ethers.utils.formatUnits(dynamicFeeInSubunits?.toString(), "6"),
  );
};

export const getAndCalculateFundingRateHyper = async (
  coinSymbol,
  tradeStartTime,
  tradeEndTime,
) => {
  const isTestnet = Object.keys(networks)?.includes("11155111");
  const url = isTestnet
    ? "https://api.hyperliquid-testnet.xyz/info"
    : "https://api.hyperliquid.xyz/info";

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "fundingHistory",
      coin: coinSymbol,
      startTime: tradeStartTime,
      endTime: tradeEndTime,
    }),
  });

  const data = await response?.json();

  if (data) {
    return data?.reduce(
      (total, item) => (total += Number(item?.fundingRate || 0)),
      0,
    );
  } else {
    return null;
  }
};

export const gcexCreateSpotOrder = async (
  security,
  cash,
  type,
  amount,
  paymentRef,
  chainId,
) => {
  return await axios
    .post("https://gateway.verified.network/api/gcex/orders", {
      security,
      cash,
      type,
      amount,
      paymentRef,
      chainId,
    })
    .then((res) => {
      if (res?.data?.body?.order_status === "NEW") {
        return true;
      } else {
        return false;
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error("create spot trade failed: ", err?.message);
      }

      return false;
    });
};

export const laserCreateSpotOrder = async (
  security,
  cash,
  type,
  amount,
  paymentRef,
  price,
  chainId,
) => {
  return await axios
    .post("https://gateway.verified.network/api/laser/order", {
      security,
      cash,
      type: "IOC",
      amount,
      paymentRef,
      side: type,
      price,
    })
    .then((res) => {
      return true;
      // if (res?.data?.body) {
      //   return true;
      // } else {
      //   return false;
      // }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error("create spot order failed: ", err?.message);
      }

      return true;
    });
};

export const blinkCreateOrder = async (userId, providerId, orderBody) => {
  return await axios
    .post("https://gateway.verified.network/api/blink/create-order", {
      user_id: userId,
      provider_id: providerId,
      orderBody,
    })
    .then((res) => {
      if (res?.data?.body?.data) {
        console.log("create order return: ", res?.data?.body?.data);
        return res?.data?.body?.data;
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.error("blink create order failed with error: ", err?.message);
      return null;
    });
};

export const getAllVerifiedCashBalances = async (
  chainId,
  web3,
  userAddress,
) => {
  const cashTokens = contractAddress[chainId]?.CASH;
  if (cashTokens && Object.values(cashTokens)?.length > 0) {
    const verifiedCashFmt = await Promise.all(
      Object.keys(cashTokens)?.map(async (tkNm) => {
        if (cashTokens[tkNm]?.length > 0) {
          const cashTokenContract = new web3.eth.Contract(
            ERC20ABI,
            cashTokens[tkNm],
          );
          const balance = await cashTokenContract.methods
            .balanceOf(userAddress)
            .call();
          return {
            name: tkNm,
            address: cashTokens[tkNm],
            balance: Number(balance) > 0 ? Number(balance) : 0,
            balanceFmt:
              Number(balance) > 0
                ? Number(ethers.utils.formatEther(Number(balance)))
                : 0,
          };
        } else {
          return null;
        }
      }),
    );

    const validverifiedCash = verifiedCashFmt?.filter((csh) => csh !== null);

    const validverifiedCashFmt = validverifiedCash?.sort(
      (a, b) => b.balance - a.balance,
    );

    if (
      (validverifiedCashFmt[0]?.balance === 0 &&
        NETWORKS_INFO[chainId]?.usdc?.length > 0) ||
      validverifiedCashFmt?.length === 0
    ) {
      const usdcContract = new web3.eth.Contract(
        ERC20ABI,
        NETWORKS_INFO[chainId]?.usdc,
      );
      const balance = await usdcContract.methods.balanceOf(userAddress).call();

      if (validverifiedCashFmt?.length > 0) {
        validverifiedCashFmt[1] = validverifiedCashFmt[0]; // go 1 back to keep the prev highest after USDC???
      }

      validverifiedCashFmt[0] = {
        name: "USDC",
        address: NETWORKS_INFO[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      };
    }

    return validverifiedCashFmt;
  } else if (NETWORKS_INFO[chainId]?.usdc && web3) {
    const usdcContract = new web3.eth.Contract(
      ERC20ABI,
      NETWORKS_INFO[chainId]?.usdc,
    );
    const balance = await usdcContract.methods.balanceOf(userAddress).call();

    return [
      {
        name: "USDC",
        address: NETWORKS_INFO[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      },
    ];
  } else {
    return;
  }
};

export const fetchAllNSDQAssets = async (page) => {
  return await axios({
    method: "GET",
    url: page
      ? `${verifiedGTNBaseUrl}/nsdq?rows=200&page=${page}`
      : `${verifiedGTNBaseUrl}/nsdq?rows=200`,
  })
    .then((res) => {
      return res.data?.body?.data?.response;
    })
    .catch((err) => {
      console.error(
        "Error while fetching all GTN NSDQ assests :",
        err?.message,
      );
      return {};
    });
};

export const convertIpfsHashToBytes32 = (ipfsHash) => {
  const cid = CID.parse(ipfsHash);
  const digest = cid.multihash.digest;
  if (digest.length !== 32) return null;
  return `0x${Buffer.from(digest)?.toString("hex")}`;
};

export const convertBytes32ToIpfsHash = (bytes32) => {
  const hex = bytes32.replace(/^0x/, "");
  const digestBytes = Uint8Array.from(Buffer.from(hex, "hex"));
  const multihash = create(0x12, digestBytes);
  const cid = CID.createV0(multihash);
  return cid.toString(base58btc);
};

export const pinFileToIpfs = async (fileInFormData) => {
  try {
    return await axios({
      method: "POST",
      url: "https://api.pinata.cloud/pinning/pinFileToIPFS",
      headers: {
        "Content-Type": "multipart/form-data",
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${config.keys.pinata}`,
      },
      data: fileInFormData,
    }).then(async (res) => {
      return res.data.IpfsHash;
    });
  } catch (err) {
    console.error("Error while uploading file to ipfs: ", err?.message);
    return;
  }
};
